// Schema.org JSON-LD generators per page type. Anything emitted here gets
// dropped into a <script type="application/ld+json"> in the document head
// by the SeoHead component, so AI scrapers and Google parse the page's
// semantic intent without rendering the SPA.
import type { OutbreakWithSources, Pathogen } from '../types/supabase';
import type { PathogenInfo } from './pathogen-data';

const SITE_URL = 'https://hantavirusmap.io';
const ORG_NAME = 'Micky Dollimore';
const ORG_URL = 'https://mickydollimore.com';

// ---------------------------------------------------------------------------
// shared base nodes
// ---------------------------------------------------------------------------

export function organizationNode() {
  return {
    '@type': 'Organization',
    '@id': `${SITE_URL}#org`,
    name: ORG_NAME,
    url: ORG_URL,
  } as const;
}

export function websiteNode() {
  return {
    '@type': 'WebSite',
    '@id': `${SITE_URL}#site`,
    name: 'hantavirusmap.io',
    url: SITE_URL,
    description:
      'Open, source-cited map of every documented hantavirus outbreak from 1993 to present. Educational use only — not medical advice.',
    inLanguage: 'en',
    publisher: { '@id': `${SITE_URL}#org` },
    license: 'https://creativecommons.org/licenses/by/4.0/',
  } as const;
}

// ---------------------------------------------------------------------------
// page-specific generators
// ---------------------------------------------------------------------------

/** Root map page — anchors a Dataset claim so AI/researchers can cite the data. */
export function mapPageJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      organizationNode(),
      websiteNode(),
      {
        '@type': 'Dataset',
        '@id': `${SITE_URL}#dataset`,
        name: 'Global hantavirus outbreaks, 1993–present',
        description:
          'Curated, source-cited dataset of documented hantavirus outbreaks worldwide. Sourced from WHO, CDC, ECDC, PAHO, national health ministries, and peer-reviewed literature.',
        url: SITE_URL,
        keywords: [
          'hantavirus', 'HPS', 'HFRS', 'orthohantavirus', 'epidemiology',
          'outbreak', 'surveillance', 'public health',
        ],
        license: 'https://creativecommons.org/licenses/by/4.0/',
        isAccessibleForFree: true,
        creator: { '@id': `${SITE_URL}#org` },
        publisher: { '@id': `${SITE_URL}#org` },
        spatialCoverage: { '@type': 'Place', name: 'Global' },
        temporalCoverage: '1993-01-01/..',
        distribution: [
          {
            '@type': 'DataDownload',
            encodingFormat: 'application/json',
            contentUrl: `${SITE_URL}/data/outbreaks.json`,
          },
        ],
      },
    ],
  };
}

/** Per-event page — Article-as-MedicalIndication. AI cites these by URL.
 *  Also emits a ClaimReview node so Google + LLM crawlers see this page as
 *  a verified-claim source. The reviewRating maps our internal confidence
 *  enum onto Google's 1-5 numeric scale. */
export function eventJsonLd(o: OutbreakWithSources) {
  const total = o.cases_confirmed + o.cases_suspected;
  // Map our 3-tier confidence onto Google's ClaimReview rating scale.
  // verified=5, preliminary=3, unverified=2. The textual rating is what
  // surfaces in rich results; the numeric value drives Google's ranking.
  const ratingMap: Record<string, { value: number; text: string; explanation: string }> = {
    verified: {
      value: 5, text: 'Verified',
      explanation: 'All numeric claims verified against verbatim quotes from tier-1 primary sources (WHO, CDC, ECDC, PAHO, or national health ministries).',
    },
    preliminary: {
      value: 3, text: 'Preliminary',
      explanation: 'Numeric claims verified against tier-2 sources (ProMED-mail, CIDRAP, peer-reviewed literature). Pending tier-1 confirmation.',
    },
    unverified: {
      value: 2, text: 'Unverified',
      explanation: 'Reported via tier-3 news sources only. Awaiting authoritative confirmation.',
    },
  };
  const rating = ratingMap[o.confidence] ?? ratingMap.preliminary;
  const claimText =
    `${o.location_name}: ${o.cases_confirmed} confirmed cases, ` +
    `${o.cases_suspected} suspected, ${o.cases_deaths} deaths ` +
    `(${o.pathogen}, ${o.syndrome}, started ${o.start_date}).`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      organizationNode(),
      {
        '@type': 'Article',
        '@id': `${SITE_URL}/event/${o.id}#article`,
        headline: o.location_name,
        url: `${SITE_URL}/event/${o.id}`,
        datePublished: o.start_date,
        dateModified: o.updated_at ?? o.ingestion_last_updated,
        description: o.summary.slice(0, 280),
        articleSection: o.syndrome === 'HPS'
          ? 'Hantavirus Pulmonary Syndrome'
          : o.syndrome === 'HFRS'
          ? 'Hemorrhagic Fever with Renal Syndrome'
          : 'Hantavirus surveillance',
        about: {
          '@type': 'MedicalCondition',
          name: o.syndrome === 'HPS'
            ? 'Hantavirus Pulmonary Syndrome'
            : 'Hemorrhagic Fever with Renal Syndrome',
        },
        publisher: { '@id': `${SITE_URL}#org` },
        author: { '@id': `${SITE_URL}#org` },
        license: 'https://creativecommons.org/licenses/by/4.0/',
        citation: o.sources.map(s => ({
          '@type': 'CreativeWork',
          name: s.title,
          publisher: s.publisher,
          url: s.url,
          dateAccessed: s.accessed,
        })),
        mentions: [
          {
            '@type': 'Place',
            name: o.location_name,
            geo: {
              '@type': 'GeoCoordinates',
              latitude: o.location_lat,
              longitude: o.location_lng,
            },
            address: { '@type': 'PostalAddress', addressCountry: o.country_iso2 },
          },
        ],
        // Surface the case totals as structured numbers — AI will cite these.
        keywords: [
          `${total} cases`,
          `${o.cases_deaths} deaths`,
          o.pathogen,
          o.country_iso2,
        ].join(', '),
      },
      // ClaimReview — eligible for Google rich-result fact-check snippet
      // and prioritised by LLM crawlers as a structured-claim source.
      {
        '@type': 'ClaimReview',
        '@id': `${SITE_URL}/event/${o.id}#claimreview`,
        url: `${SITE_URL}/event/${o.id}`,
        datePublished: o.start_date,
        author: { '@id': `${SITE_URL}#org` },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: rating.value,
          bestRating: 5,
          worstRating: 1,
          alternateName: rating.text,
          ratingExplanation: rating.explanation,
        },
        claimReviewed: claimText,
        itemReviewed: {
          '@type': 'Claim',
          appearance: o.sources.map(s => ({
            '@type': 'CreativeWork',
            url: s.url,
            name: s.title,
            publisher: s.publisher,
          })),
        },
      },
    ],
  };
}

/** Per-pathogen page — MedicalCondition. */
export function pathogenJsonLd(info: PathogenInfo) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      organizationNode(),
      {
        '@type': 'MedicalCondition',
        '@id': `${SITE_URL}/pathogen/${info.id}#condition`,
        name: info.name,
        alternateName: info.abbreviation,
        url: `${SITE_URL}/pathogen/${info.id}`,
        description: info.description.slice(0, 500),
        signOrSymptom: info.symptoms.map(s => ({
          '@type': 'MedicalSignOrSymptom',
          name: s,
        })),
        cause: {
          '@type': 'InfectiousAgent',
          name: info.name,
          infectiousAgentClass: 'Virus',
          taxon: `${info.family} > ${info.genus}`,
        },
        epidemiology: info.geographic_range.join(', '),
        // Citations propagate as `citation` references — same shape used on events.
        citation: info.citations.map(c => ({
          '@type': 'CreativeWork',
          name: c.title,
          publisher: c.publisher,
          url: c.url,
          dateAccessed: c.accessed,
        })),
      },
    ],
  };
}

/** FAQ page schema — drives rich snippets in Google search results. */
export function faqJsonLd(qas: Array<{ q: string; a: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qas.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
}

/** Generic page (about / disclaimer) — anchors organization + license. */
export function pageJsonLd(opts: { title: string; description: string; url: string }) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      organizationNode(),
      {
        '@type': 'WebPage',
        name: opts.title,
        description: opts.description,
        url: `${SITE_URL}${opts.url}`,
        publisher: { '@id': `${SITE_URL}#org` },
        license: 'https://creativecommons.org/licenses/by/4.0/',
      },
    ],
  };
}

// helper used by callers
export const SEO_SITE_URL = SITE_URL;
export const SEO_PATHOGEN_INFO_KEYS = (xs: Pathogen[]): Pathogen[] => xs;
